import {
  HorizontalTabs,
  HorizontalTabsButton,
  HorizontalTabsList,
  HorizontalTabsPanel,
  HorizontalTabsPanelList,
  Secondary,
  Text,
} from '@cinch-labs/design-system'
import { useSsrExperiments } from '@cinch-nx/adobe-server-side'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { TrackingEventTypes, useAnalytics } from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import React from 'react'
import { HomepageVehiclesData } from '../../../types/feature-home-page'
import { VehicleCondition } from '../../../types/vehicles-data'
import { Filters } from '../filters/filters'
import { HeroPartEx } from '../hero-part-ex/hero-part-ex'
import { SearchCarsForm } from '../search-cars-form/search-cars-form'
import styles from './hero-tabs.module.css'
import { PartExCta } from '../../part-ex-cta'
import { tabsNewCarsClickEvent, tabsPartExClickEvent } from './tracking'

export interface HeroTabsProps {
  title: string
  label: string
  placeholder: string
  ctaText: string
  partExTextList: string[]
  vehiclesData: HomepageVehiclesData
  section?: string
}

export const HeroTabs: React.FC<HeroTabsProps> = ({
  title,
  label,
  placeholder,
  ctaText,
  partExTextList,
  vehiclesData,
  section,
}) => {
  const { trackEvent } = useAnalytics()
  const { getExperienceIdForExperiment } = useSsrExperiments()

  const useValuationTerminology =
    getExperienceIdForExperiment(
      readFromEnv(Env.NavbarValuationExperimentId),
    ) === 1

  const isHomeHeroFormWithNewCarsTab =
    readFromEnv(Env.HomeHeroFormWithNewCarsTab) === 'true'

  const handleShopNewCarsClick = () => {
    trackEvent({
      eventName: 'Shop new cars - click',
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: 'click',
          category: 'hero',
          label: 'shop new cars',
          action: 'navigate',
        },
      },
    })
  }

  const usedCarsTabLabel = isHomeHeroFormWithNewCarsTab
    ? 'Used cars'
    : 'Find a car'
  const newCarsTabLabel = isHomeHeroFormWithNewCarsTab
    ? 'New cars'
    : useValuationTerminology
    ? 'Value my car'
    : 'Part exchange'

  return (
    <HorizontalTabs withBorder={true} activeTabId="button-id-1">
      <HorizontalTabsList ariaLabel="Find a car">
        <HorizontalTabsButton id="button-id-1" panelId="panel-id-1">
          <div className={styles.tabTitle}>{usedCarsTabLabel}</div>
        </HorizontalTabsButton>
        <HorizontalTabsButton
          id="button-id-2"
          panelId="panel-id-2"
          onButtonClick={() =>
            trackEvent(
              isHomeHeroFormWithNewCarsTab
                ? tabsNewCarsClickEvent()
                : tabsPartExClickEvent(),
            )
          }
        >
          <div className={styles.tabTitle}>{newCarsTabLabel}</div>
        </HorizontalTabsButton>
      </HorizontalTabsList>

      <HorizontalTabsPanelList>
        <HorizontalTabsPanel
          buttonId="button-id-1"
          id="panel-id-1"
          className={styles.tabPanel}
        >
          <div className={styles.formTabContainer}>
            <Text
              element={'h1'}
              className={cx(
                styles.heading,
                isHomeHeroFormWithNewCarsTab ? undefined : styles.headingMargin,
              )}
            >
              Buy used cars online
            </Text>
            <Filters
              section={section}
              makes={vehiclesData.usedVehicles.makes}
              totalStock={vehiclesData.usedVehicles.totalStock}
            />
            {isHomeHeroFormWithNewCarsTab ? (
              <PartExCta trackingValue="used" />
            ) : (
              <div className={styles.newCarsCtaContainer}>
                <p>Looking for something shiny and new?</p>
                <Secondary
                  element="a"
                  data-testid="newCarsButton"
                  href={'/new-cars'}
                  isFullWidth
                  onClick={handleShopNewCarsClick}
                >
                  {'Shop new cars'}
                </Secondary>
              </div>
            )}
          </div>
        </HorizontalTabsPanel>
        <HorizontalTabsPanel
          buttonId="button-id-2"
          id="panel-id-2"
          className={styles.tabPanel}
        >
          {isHomeHeroFormWithNewCarsTab ? (
            <div
              className={cx(styles.formTabContainer, styles.heroFormContainer)}
            >
              <Text element={'h1'} className={styles.heading}>
                Buy new cars online
              </Text>
              <SearchCarsForm
                section={section}
                vehicleData={vehiclesData.newVehicles}
                vehicleCondition={VehicleCondition.New}
              />
              <PartExCta trackingValue="used" />
            </div>
          ) : (
            <HeroPartEx
              title={title}
              ctaText={ctaText}
              label={label}
              placeholder={placeholder}
              partExTextList={partExTextList}
            />
          )}
        </HorizontalTabsPanel>
      </HorizontalTabsPanelList>
    </HorizontalTabs>
  )
}
